import React, { useEffect, useState } from 'react'
import {
  Box,
  IconButton,
  Typography,
  Toolbar,
  TextField,
  Alert,
  Autocomplete,
  Button,
  Chip,
  Drawer,
} from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import AddIcon from '@mui/icons-material/Add'
import CloseIcon from '@mui/icons-material/Close'
import AttachFileIcon from '@mui/icons-material/AttachFile'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import { useQuery, useLazyQuery } from '@apollo/react-hooks'
import { GET_CATEGORIES, GET_DOWNLOAD_LINK } from './gql'
import { VoucherType, VoucherCostCategory, CostCategory } from '../../types'
import Receipts from './Receipts'
import VhsDialog from '../Common/VhsDialog'

// Types for the component props and callbacks
export interface CostCategoriesProps {
  voucher: VoucherType
  readonly?: boolean
  onAdd?: (
    costCategory: Omit<VoucherCostCategory, 'id'>,
  ) => Promise<VoucherCostCategory | void>
  onUpdate?: (
    id: string,
    costCategory: Partial<VoucherCostCategory>,
  ) => Promise<void>
  onDelete?: (id: string) => Promise<void>
  onOpenHelp?: () => void
}

// Types for internal state
interface EditingState {
  id: string | null
  cost_category_id: string
  cost_share: number
  comment: string
}

// Distinguishes parent-category groups by a stripe color on each card.
const STRIPE_COLORS = [
  '#2F6F62',
  '#96703E',
  '#5B87C9',
  '#8659BD',
  '#BD5F5F',
  '#4C9A8F',
]

const MAX_INLINE_RECEIPTS = 3

// Truncates in the middle so the file extension - the part people actually
// recognize a file by - always stays visible, instead of end-ellipsis
// cutting it off.
const truncateMiddle = (name: string, max: number): string => {
  if (!name || name.length <= max) return name
  const dot = name.lastIndexOf('.')
  const ext = dot > 0 ? name.slice(dot) : ''
  const base = dot > 0 ? name.slice(0, dot) : name
  const keep = max - ext.length - 1
  if (keep < 6) return name.slice(0, max - 1) + '…'
  const front = Math.ceil(keep * 0.6)
  const back = keep - front
  return base.slice(0, front) + '…' + base.slice(base.length - back) + ext
}

const CostCategories: React.FC<CostCategoriesProps> = ({
  voucher,
  readonly = false,
  onAdd,
  onUpdate,
  onDelete,
  onOpenHelp,
}) => {
  // Fetch categories for dropdowns
  const {
    data: categoriesData,
    loading: categoriesLoading,
    error: categoriesError,
  } = useQuery(GET_CATEGORIES)
  const categories: CostCategory[] = categoriesData?.costCategories || []

  // Opens a receipt in a new tab, same as clicking one in the old design.
  const [getFileDownloadLink] = useLazyQuery(GET_DOWNLOAD_LINK, {
    onCompleted: (data) => {
      if (data?.signedDownloadLink?.signedLink) {
        window.open(data.signedDownloadLink.signedLink, '_blank')
      }
    },
  })

  // State for editing mode
  const [editingState, setEditingState] = useState<EditingState | null>(null)
  const [isAdding, setIsAdding] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [deleteId, setDeleteId] = useState<string | null>(null)
  const [expandedReceipts, setExpandedReceipts] = useState<
    Record<string, boolean>
  >({})

  const drawerOpen = isAdding || editingState !== null

  // Reset editing state when voucher changes
  useEffect(() => {
    setEditingState(null)
    setIsAdding(false)
    setError(null)
  }, [voucher.id])

  // Handle adding new cost category
  const handleAddCostCategory = () => {
    setIsAdding(true)
    setEditingState({
      id: null,
      cost_category_id: '',
      cost_share: 0,
      comment: '',
    })
    setError(null)
  }

  // Handle editing existing cost category
  const handleEditCostCategory = (costCategory: VoucherCostCategory) => {
    setEditingState({
      id: costCategory.id?.toString() || '',
      cost_category_id: costCategory.sub_cost_category.id,
      cost_share: costCategory.cost_share,
      comment: costCategory.comment || '',
    })
    setIsAdding(false)
    setError(null)
  }

  // Handle canceling edit/add mode
  const handleCancelEdit = () => {
    setEditingState(null)
    setIsAdding(false)
    setError(null)
  }

  // Handle saving cost category (add or update)
  const handleSaveCostCategory = async () => {
    if (!editingState) {
      console.log('No editing state, returning')
      return
    }

    const { cost_category_id, cost_share, comment } = editingState

    if (!cost_category_id) {
      console.log('Validation failed - missing category')
      setError('Please select a category.')
      return
    }

    if (cost_share <= 0) {
      console.log('Validation failed - invalid cost share')
      setError('Please enter a valid cost share amount greater than 0.')
      return
    }

    // Check if the selected category is a valid child category (not a parent category)
    if (cost_category_id.startsWith('parent-')) {
      console.log('Validation failed - parent category selected')
      setError('Please select a child category, not a parent category.')
      return
    }

    try {
      setError(null)
      if (isAdding && onAdd) {
        const selectedSubCategory = categories
          .flatMap((c) => c.sub_cost_categories)
          .find((sc) => sc.id === cost_category_id)
        if (!selectedSubCategory) {
          throw new Error('Selected category not found')
        }

        const created = await onAdd({
          sub_cost_category: selectedSubCategory,
          cost_share,
          comment,
        })

        if (created?.id) {
          // Stay open, but switch to editing the category that was just
          // created - receipts can only attach to a saved category, so
          // this lets the user upload one in the same go.
          setIsAdding(false)
          setEditingState({
            id: created.id.toString(),
            cost_category_id,
            cost_share,
            comment,
          })
        } else {
          setEditingState(null)
          setIsAdding(false)
        }
      } else if (editingState.id && onUpdate) {
        const selectedSubCategory = categories
          .flatMap((c) => c.sub_cost_categories)
          .find((sc) => sc.id === cost_category_id)
        if (!selectedSubCategory) {
          throw new Error('Selected category not found')
        }
        await onUpdate(editingState.id, {
          sub_cost_category: selectedSubCategory,
          cost_share,
          comment,
        })
        setEditingState(null)
        setIsAdding(false)
      }
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to save cost category',
      )
    }
  }

  // Handle deleting cost category
  const handleDeleteCostCategory = async (id: string) => {
    if (!onDelete) return
    setDeleteId(id)
  }

  const handleConfirmDelete = async () => {
    if (!deleteId || !onDelete) return

    try {
      setError(null)
      await onDelete(deleteId)
      setDeleteId(null)
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to delete cost category',
      )
    }
  }

  // Handle editing state changes
  const handleEditingStateChange = (
    field: keyof EditingState,
    value: string | number,
  ) => {
    if (!editingState) return

    setEditingState((prev) =>
      prev
        ? {
            ...prev,
            [field]: value,
          }
        : null,
    )
  }

  // Find the parent category of a given sub-category
  const findParentCategoryBySubId = (subCategoryId: string) => {
    return categories.find((c) =>
      c.sub_cost_categories.some((sc) => sc.id === subCategoryId),
    )
  }

  const getStripeColor = (subCategoryId: string) => {
    const parent = findParentCategoryBySubId(subCategoryId)
    const index = parent ? categories.findIndex((c) => c.id === parent.id) : 0
    return STRIPE_COLORS[Math.max(index, 0) % STRIPE_COLORS.length]
  }

  if (categoriesLoading) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography>Loading categories...</Typography>
      </Box>
    )
  }

  if (categoriesError) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">
          Failed to load categories: {categoriesError.message}
        </Alert>
      </Box>
    )
  }

  // The category currently open in the drawer, if editing an existing one -
  // used to feed the real cost category (and its receipts) into <Receipts>.
  const editingCostCategory = editingState?.id
    ? voucher.voucher_cost_category.find(
        (cc) => cc.id?.toString() === editingState.id,
      )
    : undefined

  return (
    <Box
      sx={{
        py: 2,
        px: { xs: 1, sm: 2 },
        mb: 2,
        backgroundColor: 'background.paper',
      }}
    >
      <Toolbar variant="dense" disableGutters>
        <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
          Cost Categories
        </Typography>
        {onOpenHelp && (
          <IconButton size="small" onClick={onOpenHelp} color="primary">
            <HelpOutlineIcon fontSize="small" />
          </IconButton>
        )}
        {!readonly && (
          <Button
            variant="outlined"
            size="small"
            startIcon={<AddIcon />}
            onClick={handleAddCostCategory}
            disabled={drawerOpen}
            sx={{ ml: 1 }}
          >
            Add category
          </Button>
        )}
      </Toolbar>

      {error && !drawerOpen && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Cost Category Cards */}
      {voucher.voucher_cost_category.length > 0 && (
        <Box
          sx={{
            display: 'grid',
            // Column count follows the container's width, not the viewport's -
            // the cards sit in a narrower main column beside the sidebar.
            gridTemplateColumns: 'repeat(auto-fill, minmax(min(260px, 100%), 1fr))',
            gap: 2.5,
            alignItems: 'start',
          }}
        >
          {voucher.voucher_cost_category.map((costCategory) => {
            const id = costCategory.id?.toString() || ''
            const parent = findParentCategoryBySubId(
              costCategory.sub_cost_category.id,
            )
            const receipts = costCategory.receipts || []
            const isReceiptsExpanded = !!expandedReceipts[id]
            const visibleReceipts = isReceiptsExpanded
              ? receipts
              : receipts.slice(0, MAX_INLINE_RECEIPTS)
            const overflowReceipts = isReceiptsExpanded
              ? []
              : receipts.slice(MAX_INLINE_RECEIPTS)

            return (
              <Box
                key={id}
                sx={{
                  border: 1,
                  borderColor: 'divider',
                  borderTop: 4,
                  borderTopColor: getStripeColor(
                    costCategory.sub_cost_category.id,
                  ),
                  borderRadius: 2,
                  p: 2.5,
                  minWidth: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1.5,
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    flexWrap: 'wrap',
                    gap: 1,
                  }}
                >
                  <Box sx={{ minWidth: 0, maxWidth: '100%' }}>
                    <Typography variant="subtitle1" fontWeight={700} noWrap>
                      {costCategory.sub_cost_category.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" noWrap>
                      {parent?.name ? `${parent.name} · ` : ''}
                      {costCategory.sub_cost_category.code}
                    </Typography>
                  </Box>
                  <Typography
                    variant="subtitle1"
                    fontWeight={700}
                    sx={{
                      fontFamily: 'monospace',
                      fontVariantNumeric: 'tabular-nums',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {voucher.currency} {costCategory.cost_share.toFixed(2)}
                  </Typography>
                </Box>

                {!!costCategory.comment && (
                  <Typography
                    variant="body1"
                    title={costCategory.comment}
                    sx={{
                      fontStyle: 'italic',
                      color: 'text.secondary',
                      cursor: 'help',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                    }}
                  >
                    &ldquo;{costCategory.comment}&rdquo;
                  </Typography>
                )}

                {receipts.length > 0 && (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      gap: 0.75,
                    }}
                  >
                    {visibleReceipts.map((r) => (
                      <Chip
                        key={r.id}
                        variant="outlined"
                        icon={<AttachFileIcon sx={{ fontSize: 18 }} />}
                        label={truncateMiddle(r.path, 26)}
                        title={r.path}
                        clickable
                        onClick={() =>
                          getFileDownloadLink({ variables: { id: r.id } })
                        }
                        sx={{
                          maxWidth: '100%',
                          fontSize: '0.875rem',
                          height: 32,
                        }}
                      />
                    ))}
                    {overflowReceipts.length > 0 && (
                      <Typography
                        component="button"
                        type="button"
                        variant="body2"
                        color="primary"
                        onClick={() =>
                          setExpandedReceipts((prev) => ({
                            ...prev,
                            [id]: true,
                          }))
                        }
                        sx={{
                          cursor: 'pointer',
                          background: 'none',
                          border: 'none',
                          p: 0,
                          font: 'inherit',
                          textDecoration: 'underline',
                        }}
                      >
                        +{overflowReceipts.length} more
                      </Typography>
                    )}
                    {isReceiptsExpanded && receipts.length > MAX_INLINE_RECEIPTS && (
                      <Typography
                        component="button"
                        type="button"
                        variant="body2"
                        color="primary"
                        onClick={() =>
                          setExpandedReceipts((prev) => ({
                            ...prev,
                            [id]: false,
                          }))
                        }
                        sx={{
                          cursor: 'pointer',
                          background: 'none',
                          border: 'none',
                          p: 0,
                          font: 'inherit',
                          textDecoration: 'underline',
                        }}
                      >
                        Show less
                      </Typography>
                    )}
                  </Box>
                )}

                {!readonly && (
                  <Box
                    sx={{
                      display: 'flex',
                      gap: 1.5,
                      mt: 'auto',
                      pt: 1.5,
                    }}
                  >
                    <Button
                      variant="outlined"
                      startIcon={<EditIcon />}
                      onClick={() => handleEditCostCategory(costCategory)}
                      disabled={drawerOpen}
                      sx={{ flex: 1, fontSize: '0.9rem' }}
                    >
                      Edit
                    </Button>
                    <Button
                      variant="outlined"
                      color="error"
                      startIcon={<DeleteIcon />}
                      onClick={() => handleDeleteCostCategory(id)}
                      disabled={drawerOpen}
                      sx={{ flex: 1, fontSize: '0.9rem' }}
                    >
                      Delete
                    </Button>
                  </Box>
                )}
              </Box>
            )
          })}
        </Box>
      )}

      {/* Empty State */}
      {voucher.voucher_cost_category.length === 0 && (
        <Box sx={{ p: 2, textAlign: 'center' }}>
          <Typography color="text.secondary">
            No cost categories added yet.
            {!readonly && ' Click Add category to add one.'}
          </Typography>
        </Box>
      )}

      {/* Add/Edit Drawer */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={handleCancelEdit}
        PaperProps={{ sx: { width: { xs: '100%', sm: 480 } } }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2,
            py: 1.5,
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <Typography variant="subtitle1" fontWeight={600}>
            {isAdding ? 'Add Cost Category' : 'Edit Cost Category'}
          </Typography>
          <IconButton size="small" onClick={handleCancelEdit}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <Box
          sx={{
            p: 2,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            flex: 1,
            overflowY: 'auto',
          }}
        >
          {error && (
            <Alert severity="error" sx={{ mb: 0 }}>
              {error}
            </Alert>
          )}

          <Autocomplete
            value={
              categories
                .flatMap((c) => c.sub_cost_categories)
                .find(
                  (sc) => sc.id === (editingState?.cost_category_id || ''),
                ) || null
            }
            options={categories.flatMap((c) => c.sub_cost_categories)}
            groupBy={(option) =>
              findParentCategoryBySubId(option.id)?.name || 'Other'
            }
            getOptionLabel={(option) => `${option.name} (${option.code})`}
            filterOptions={(options, state) => {
              const { inputValue } = state
              const search = inputValue.toLowerCase()
              return options.filter((option) => {
                const parent = findParentCategoryBySubId(option.id)
                return (
                  option.name.toLowerCase().includes(search) ||
                  option.code.toLowerCase().includes(search) ||
                  (parent && parent.name.toLowerCase().includes(search))
                )
              })
            }}
            onChange={(event: any, newValue: any) => {
              handleEditingStateChange(
                'cost_category_id',
                newValue ? newValue.id : '',
              )
            }}
            disabled={readonly}
            renderInput={(params: any) => (
              <TextField
                {...params}
                label="Cost Category"
                variant="outlined"
                size="small"
                placeholder="Select cost category"
              />
            )}
            renderOption={(props: any, option) => (
              <li {...props} key={option.id}>
                {option.name} ({option.code})
              </li>
            )}
            isOptionEqualToValue={(option, value) => option.id === value.id}
          />

          <TextField
            label="Cost Share"
            fullWidth
            size="small"
            type="number"
            value={editingState?.cost_share || ''}
            variant="outlined"
            disabled={readonly}
            onChange={(e) =>
              handleEditingStateChange(
                'cost_share',
                parseFloat(e.target.value) || 0,
              )
            }
            inputProps={{ min: 0, step: 0.01 }}
            placeholder="Enter amount"
          />

          <TextField
            label="Comment"
            fullWidth
            size="small"
            multiline
            rows={3}
            value={editingState?.comment || ''}
            variant="outlined"
            disabled={readonly}
            onChange={(e) =>
              handleEditingStateChange('comment', e.target.value)
            }
            placeholder="Additional information about this cost share"
          />

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Receipts
            </Typography>
            {isAdding ? (
              <Alert severity="info" variant="outlined">
                Save the category first &mdash; receipts attach to a saved
                cost category.
              </Alert>
            ) : (
              editingCostCategory && (
                <Receipts
                  voucherId={voucher.id}
                  voucherCostCategory={editingCostCategory}
                  onCategoryChange={(
                    categoryId: string | number,
                    updatedCategory: Partial<VoucherCostCategory>,
                  ) => {
                    if (onUpdate) {
                      onUpdate(categoryId.toString(), updatedCategory)
                    }
                  }}
                  voucherReceipts={editingCostCategory.receipts || []}
                  paidByAmin={voucher.paid_by_amin}
                  readonly={readonly}
                />
              )
            )}
          </Box>
        </Box>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 1,
            px: 2,
            py: 1.5,
            borderTop: 1,
            borderColor: 'divider',
          }}
        >
          <Button onClick={handleCancelEdit}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveCostCategory}>
            Save changes
          </Button>
        </Box>
      </Drawer>

      {/* Delete Confirmation Dialog */}
      <VhsDialog
        open={deleteId !== null}
        onClose={() => setDeleteId(null)}
        onConfirm={handleConfirmDelete}
        title="Delete Cost Category"
        warningMessage="Are you sure you want to delete this cost category?"
        confirmButtonText="Delete"
        color="error"
        showReasonField={false}
      />
    </Box>
  )
}

export default CostCategories
